import { debounce } from 'lodash';

import {
  sendAnalyticsError,
  sendAnalyticsInfo,
} from 'features/analytics/helpers';
import type { AppDispatch } from 'features/app/store/store';

export interface DataChannelError extends Error {
  code?: string;
  type?: string;
  context?: Record<string, unknown>;
}

export interface DataChannelMetrics {
  messagesReceived: number;
  messagesSent: number;
}

// CONSTANTS

export const DATA_CHANNEL_LOG_GROUP = 'VrsChat';
export const WEBRTC_LOG_GROUP = 'WebRTC';

// HELPERS

/**
 * Creates an error with additional context information
 */
export const createDataChannelError = (
  message: string,
  context: Record<string, unknown>
): DataChannelError => {
  const error = new Error(message) as DataChannelError;
  error.context = context;
  return error;
};

/**
 * Factory for creating debounced analytics trackers.
 * Used to batch and debounce frequent DataChannel message events.
 */
export const createDataChannelAnalyticsTracker = (
  dispatch: AppDispatch,
  method: string,
  messageTemplate: (count: number) => string,
  debounceMs: number
) => {
  let count = 0;

  const debouncedFn = debounce(() => {
    if (count > 0) {
      dispatch(
        sendAnalyticsInfo({
          Method: method,
          Message: messageTemplate(count),
          LogGroup: DATA_CHANNEL_LOG_GROUP,
        })
      );
      count = 0;
    }
  }, debounceMs);

  return Object.assign(
    () => {
      count++;
      debouncedFn();
    },
    {
      flush: () => debouncedFn.flush(),
      cancel: () => debouncedFn.cancel(),
    }
  );
};

// ANALYTICS LOGGING FUNCTIONS

/**
 * Logs DataChannel cleanup with final metrics
 */
export const logDataChannelCleanup = (
  dispatch: AppDispatch,
  reason: string,
  metrics: DataChannelMetrics
): void => {
  dispatch(
    sendAnalyticsInfo({
      Method: 'closeAndCleanupChannel',
      Message: `data channel cleanup: ${reason}, metrics: ${JSON.stringify(metrics)}`,
      LogGroup: DATA_CHANNEL_LOG_GROUP,
    })
  );
};

/**
 * Logs DataChannel open timeout error
 */
export const logDataChannelOpenTimeout = (
  dispatch: AppDispatch,
  dataChannel: RTCDataChannel
): void => {
  dispatch(
    sendAnalyticsError({
      error: createDataChannelError('datachannel open timeout', {
        readyState: dataChannel.readyState,
        bufferedAmount: dataChannel.bufferedAmount,
      }),
      Method: 'createDataChannel',
      LogGroup: DATA_CHANNEL_LOG_GROUP,
    })
  );
};

/**
 * Logs DataChannel error event
 */
export const logDataChannelError = (
  dispatch: AppDispatch,
  event: Event,
  dataChannel: RTCDataChannel
): void => {
  const error = createDataChannelError(`DataChannel error: ${event.type}`, {
    readyState: dataChannel.readyState,
    bufferedAmount: dataChannel.bufferedAmount,
    protocol: dataChannel.protocol,
  });

  dispatch(
    sendAnalyticsError({
      error,
      Method: 'createDataChannel.onerror',
      LogGroup: DATA_CHANNEL_LOG_GROUP,
    })
  );
};

/**
 * Logs concurrent DataChannel creation attempt error
 */
export const logDataChannelConcurrentCreation = (
  dispatch: AppDispatch
): void => {
  dispatch(
    sendAnalyticsError({
      error: new Error('Channel creation already in progress'),
      Method: 'createDataChannel',
      LogGroup: DATA_CHANNEL_LOG_GROUP,
    })
  );
};

/**
 * Logs DataChannel creation exception
 */
export const logDataChannelCreationException = (
  dispatch: AppDispatch,
  error: Error
): void => {
  dispatch(
    sendAnalyticsError({
      error,
      Method: 'createDataChannel',
      LogGroup: DATA_CHANNEL_LOG_GROUP,
    })
  );
};

/**
 * Logs DataChannel send message failure
 */
export const logDataChannelSendFailed = (
  dispatch: AppDispatch,
  channel: RTCDataChannel,
  messageSize: number
): void => {
  dispatch(
    sendAnalyticsError({
      error: createDataChannelError('Failed to send message', {
        readyState: channel.readyState,
        bufferedAmount: channel.bufferedAmount,
        messageSize,
      }),
      Method: 'sendMessage',
      LogGroup: DATA_CHANNEL_LOG_GROUP,
    })
  );
};

export interface DataChannelStateCounts {
  total: number;
  open: number;
  connecting: number;
  closing: number;
  closed: number;
}

const EMPTY_STATE_COUNTS: DataChannelStateCounts = {
  total: 0,
  open: 0,
  connecting: 0,
  closing: 0,
  closed: 0,
};

/**
 * Async count of data channels on a peer connection via getStats().
 * Counts data channels by state to detect multi-channel persistence issues.
 */
export const getDataChannelStateCountsAsync = async (
  peerConnection: RTCPeerConnection | null | undefined
): Promise<DataChannelStateCounts> => {
  if (!peerConnection) return { ...EMPTY_STATE_COUNTS };

  const result: DataChannelStateCounts = { ...EMPTY_STATE_COUNTS };

  try {
    const stats = await peerConnection.getStats();
    stats.forEach((report) => {
      if (report.type === 'data-channel') {
        result.total++;
        switch (report.state) {
          case 'open':
            result.open++;
            break;
          case 'connecting':
            result.connecting++;
            break;
          case 'closing':
            result.closing++;
            break;
          case 'closed':
            result.closed++;
            break;
        }
      }
    });
  } catch {
    return { ...EMPTY_STATE_COUNTS };
  }

  return result;
};

export const sendDataChannelOpenedAnalytics = (
  dispatch: AppDispatch,
  dataChannel: RTCDataChannel
) => {
  dispatch(
    sendAnalyticsInfo({
      Method: 'setupDataChannelHandlers.onopen',
      LogGroup: DATA_CHANNEL_LOG_GROUP,
      Message: {
        event: 'data_channel_opened',
        label: dataChannel.label,
        id: dataChannel.id,
        protocol: dataChannel.protocol,
      },
    })
  );
};

export const sendDataChannelClosingAnalytics = (
  dispatch: AppDispatch,
  event: Event,
  dataChannel: RTCDataChannel
) => {
  dispatch(
    sendAnalyticsInfo({
      Method: 'setupDataChannelHandlers.onclosing',
      LogGroup: DATA_CHANNEL_LOG_GROUP,
      Message: {
        event: 'data_channel_closing',
        eventType: event.type,
        readyState: dataChannel.readyState,
        label: dataChannel.label,
        id: dataChannel.id,
      },
    })
  );
};

export const sendCreateDataChannelSkippedAnalytics = (
  dispatch: AppDispatch,
  existingReadyState: RTCDataChannel['readyState']
) => {
  dispatch(
    sendAnalyticsInfo({
      Method: 'createDataChannel',
      Message: {
        event: 'data_channel_creation_skipped',
        reason: 'existing_channel_healthy',
        existingReadyState,
      },
    })
  );
};

/**
 * Combined handler for data channel `close` events.
 * Sends the close analytics then logs the final metrics.
 */
export const handleDataChannelClosed = (
  dispatch: AppDispatch,
  dataChannel: RTCDataChannel,
  metrics: DataChannelMetrics
): void => {
  dispatch(
    sendAnalyticsInfo({
      Method: 'setupDataChannelHandlers.onclose',
      LogGroup: DATA_CHANNEL_LOG_GROUP,
      Message: {
        event: 'data_channel_closed',
        label: dataChannel.label,
        id: dataChannel.id,
        readyState: dataChannel.readyState,
        messagesReceived: metrics.messagesReceived,
        messagesSent: metrics.messagesSent,
      },
    })
  );
};

export const sendDataChannelStateCountsAtCleanup = (
  dispatch: AppDispatch,
  stateCounts: DataChannelStateCounts,
  logGroup?: string
) => {
  dispatch(
    sendAnalyticsInfo({
      Method: 'cleanupSession',
      LogGroup: logGroup,
      Message: {
        event: 'data_channel_state_counts_at_cleanup',
        ...stateCounts,
      },
    })
  );
};
